import React, { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiClient } from "@/api/client"
import { CategoryDto } from "@/api/api-client"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Plus, Trash2, Pencil, FolderTree } from "lucide-react"
import { toastSuccess, toastError } from "@/lib/toast"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"
import { useLanguage } from "@/hooks/useLanguage"

interface CategoryManagementProps {}

export const CategoryManagement: React.FC<CategoryManagementProps> = () => {
  const queryClient = useQueryClient()
  const { t, language } = useLanguage()
  const text = t.categoryManagement
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<CategoryDto | null>(null)
  const [formData, setFormData] = useState({
    nameEs: "",
    nameEn: "",
    parentId: undefined as number | undefined,
  })

  const { data: categories, isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: () => apiClient.getCategoryTree(),
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => apiClient.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] })
      setIsSheetOpen(false)
      resetForm()
      toastSuccess(text.successCreate)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: any) => apiClient.updateCategory(editingCategory!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] })
      setIsSheetOpen(false)
      setEditingCategory(null)
      resetForm()
      toastSuccess(text.successUpdate)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiClient.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] })
      toastSuccess(text.successDelete)
    },
    onError: (error: any) => {
      toastError(text.errorDelete + error.message)
    },
  })

  const resetForm = () => {
    setFormData({ nameEs: "", nameEn: "", parentId: undefined })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingCategory) {
      updateMutation.mutate(formData)
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleEdit = (cat: CategoryDto) => {
    setEditingCategory(cat)
    setFormData({
      nameEs: cat.nameEs,
      nameEn: cat.nameEn,
      parentId: cat.parentId || undefined,
    })
    setIsSheetOpen(true)
  }

  const flattenCategories = (cats: CategoryDto[], depth = 0): any[] => {
    return cats.flatMap((cat) => [
      { id: cat.id, name: language === "es" ? cat.nameEs : cat.nameEn, depth },
      ...flattenCategories(cat.subcategories || [], depth + 1),
    ])
  }

  const flatCats = categories ? flattenCategories(categories) : []

  const renderRows = (cats: CategoryDto[], depth = 0) => {
    return cats.flatMap((cat) => [
      <TableRow key={cat.id} className="border-b border-border transition-colors hover:bg-muted/5">
        <TableCell style={{ paddingLeft: `${depth * 24 + 16}px` }} className="py-6">
          <div className="flex items-center gap-3">
            <div className={cn("h-px w-4 bg-accent/20", depth === 0 && "hidden")} />
            <FolderTree
              className={cn(
                "h-4 w-4",
                depth === 0 ? "text-primary" : "text-muted-foreground opacity-50"
              )}
            />
            <span
              className={cn(
                "font-display text-xl tracking-wider",
                depth === 0 ? "text-foreground" : "text-muted-foreground"
              )}
            >
              {cat.nameEs}
            </span>
            <span className="px-1 text-muted-foreground/30">/</span>
            <span className="text-sm font-medium italic text-muted-foreground/60">
              {cat.nameEn}
            </span>
          </div>
        </TableCell>
        <TableCell className="py-6 pr-0 text-right">
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-lg transition-colors hover:bg-accent/10 hover:text-accent"
              onClick={() => handleEdit(cat)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
              onClick={() => {
                if (confirm(text.deleteConfirm)) {
                  deleteMutation.mutate(cat.id)
                }
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>,
      ...renderRows(cat.subcategories || [], depth + 1),
    ])
  }

  return (
    <div className="space-y-10">
      <div className="flex flex-col items-start justify-between gap-6 border-b-2 border-border pb-8 md:flex-row md:items-end">
        <div>
          <h2 className="mb-2 font-display text-4xl leading-none tracking-tighter text-foreground">
            {text.title}
          </h2>
          <p className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
            {text.subtitle}
          </p>
        </div>

        <Sheet
          open={isSheetOpen}
          onOpenChange={(open) => {
            setIsSheetOpen(open)
            if (!open) {
              setEditingCategory(null)
              resetForm()
            }
          }}
        >
          <SheetTrigger asChild>
            <Button className="h-12 rounded-lg bg-primary px-8 font-display tracking-widest text-primary-foreground shadow-md hover:opacity-90">
              <Plus className="mr-2 h-4 w-4" />
              {text.add}
            </Button>
          </SheetTrigger>
          <SheetContent className="flex h-full w-full flex-col overflow-hidden border-l border-border bg-muted p-0 shadow-2xl sm:max-w-[500px]">
            <div className="flex h-full flex-col px-1">
              <SheetHeader className="border-b bg-background p-6">
                <SheetTitle className="font-display text-2xl tracking-widest">
                  {editingCategory ? text.edit : text.add}
                </SheetTitle>
              </SheetHeader>

              <ScrollArea className="flex-1">
                <div className="p-6">
                  <form id="category-form" onSubmit={handleSubmit} className="space-y-8">
                    <div className="space-y-2">
                      <Label htmlFor="nameEs" className="label-industrial">
                        {text.nameEs}
                      </Label>
                      <Input
                        id="nameEs"
                        required
                        value={formData.nameEs}
                        onChange={(e) => setFormData({ ...formData, nameEs: e.target.value })}
                        className="h-12 text-base"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nameEn" className="label-industrial">
                        {text.nameEn}
                      </Label>
                      <Input
                        id="nameEn"
                        required
                        value={formData.nameEn}
                        onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                        className="h-12 text-base"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="parentId" className="label-industrial">
                        {text.parent}
                      </Label>
                      <select
                        id="parentId"
                        className="flex h-12 w-full cursor-pointer appearance-none rounded-lg border border-input bg-background px-3 py-2 text-base transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        value={formData.parentId || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            parentId: e.target.value ? parseInt(e.target.value) : undefined,
                          })
                        }
                      >
                        <option value="">{text.none}</option>
                        {flatCats
                          .filter((c) => c.id !== editingCategory?.id)
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {"\u00A0".repeat(c.depth * 4)}
                              {c.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </form>{" "}
                </div>
              </ScrollArea>

              <SheetFooter className="border-t bg-muted/10 p-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsSheetOpen(false)}
                  className="font-display tracking-widest"
                >
                  {text.cancel}
                </Button>
                <Button
                  type="submit"
                  form="category-form"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="h-12 bg-primary px-8 font-display tracking-widest text-primary-foreground shadow-md hover:opacity-90"
                >
                  {createMutation.isPending || updateMutation.isPending ? "..." : text.save}
                </Button>
              </SheetFooter>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="border-none bg-transparent">
        <Table>
          <TableHeader>
            <TableRow className="border-b-2 border-border hover:bg-transparent">
              <TableHead className="px-0 font-display text-lg tracking-widest text-foreground">
                {text.categoryLabel}
              </TableHead>
              <TableHead className="px-0 text-right font-display text-lg tracking-widest text-foreground">
                {text.actions}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={2} className="py-20 text-center">
                  <div className="animate-pulse font-display text-2xl tracking-widest opacity-20">
                    {text.loading}
                  </div>
                </TableCell>
              </TableRow>
            ) : categories && categories.length > 0 ? (
              renderRows(categories)
            ) : (
              <TableRow>
                <TableCell colSpan={2} className="empty-state py-32">
                  <FolderTree className="mx-auto mb-4 h-12 w-12 text-muted-foreground opacity-10" />
                  <p className="font-display text-2xl tracking-widest text-muted-foreground opacity-20">
                    {text.empty}
                  </p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

